import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  MdEventBusy,
  MdEdit,
  MdUndo,
  MdChevronLeft,
  MdChevronRight,
  MdCalendarMonth,
  MdRefresh,
} from 'react-icons/md'
import type { DeliveryDateOverride, RoundDayAmendment, SubscriptionLine } from '@/types'
import type { DeliveryDate } from '@/utils/deliverySchedule'
import { toStartOfDay } from '@/utils/deliverySchedule'
import { toLocalDateString } from '@/utils/deliveryDays'
import { formatDate as formatFullDate, formatDayAndMonth } from '@/utils/dateFormat'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import OneOffDeliveriesBox from './OneOffDeliveriesBox'
import { frequencyTag } from '@/utils/billingFrequency'
import { formatCutoffTime, movedDeliveryTargets, periodFor, schedulePeriods, shownDateLookup } from '@/utils/deliveryOverlay'
import { ORDER_EDITING_DISABLED_PHONE as CUSTOMER_SERVICE_PHONE } from '@/config/maintenance'
import { CHRISTMAS_CHANGES_BY } from '@/config/christmas'
import ScheduleInfoModal, { type ScheduleInfo } from './ScheduleInfoModal'
import '@/styles/components/daily-delivery-card.css'

const PRODUCT_IMAGE = '/milk-bottle.jpg'

const formatPrice = (price: number): string => `£${price.toFixed(2)}`

const WEEKDAY_INITIALS = ['M', 'T', 'W', 'T', 'F', 'S', 'S']
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** A YYYY-MM-DD date as local midnight (new Date('YYYY-MM-DD') would be UTC). */
const localDate = (value: string): Date => {
  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

/**
 * A day's items, fewest first. The eye lands on the last thumbnail, so putting
 * the larger quantities there is where the badges do most good. Sorted by name
 * within a quantity so the order holds still between renders.
 */
const byQuantity = (items: SubscriptionLine[]): SubscriptionLine[] =>
  [...items].sort((a, b) => a.quantity - b.quantity
    || (a.product_name || a.name || '').localeCompare(b.product_name || b.name || ''))

/**
 * A day the customer sees as paused: a holiday that actually covers a delivery.
 * A holiday over a day with no order (a round delivery day the customer takes
 * nothing on) is not shown as paused, since there was never a delivery to
 * pause; that day reads blank like any other empty day (FEDEV-615).
 */
const isPausedDelivery = (d: DeliveryDate): boolean => d.isHoliday && d.items.length > 0

/** The Monday of the week a date falls in. */
const startOfWeek = (date: Date): Date => {
  const d = toStartOfDay(date)
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d
}

/**
 * A month as Monday-first weeks, padded with nulls so every row has 7 cells.
 */
const buildMonth = (year: number, month: number): (Date | null)[][] => {
  const first = new Date(year, month, 1)
  const daysInMonth = new Date(year, month + 1, 0).getDate()

  const cells: (Date | null)[] = []
  // getDay() is Sunday-first; shift so Monday starts the week.
  const leadingBlanks = (first.getDay() + 6) % 7
  for (let i = 0; i < leadingBlanks; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d))
  while (cells.length % 7 !== 0) cells.push(null)

  const weeks: (Date | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

interface DeliveryScheduleProps {
  deliveries: DeliveryDate[]
  loading?: boolean
  /** Skip just the given delivery. Absent while the account is restricted. */
  onSkipDay?: (date: Date) => void
  /** Un-skip a delivery that's currently paused. */
  onCancelSkip?: (date: Date) => void
  skipDisabled?: boolean
  /**
   * The earliest date (YYYY-MM-DD) a delivery can still be paused, from the
   * server's holiday cutoff (evaluated in UK time). A delivery before this is
   * past the 5pm cutoff, so we don't offer Pause on it (FEDEV-532).
   */
  minPauseDate?: string
  /** Open a product's detail from a delivery's contents. */
  onViewProduct?: (productId: string, name: string, unitPrice: number) => void
  /** Weekly delivery surcharge (a hidden product line, SKU 999). Added to the
   *  week total and shown as a note beneath it, never as a delivery (DF-88). */
  surchargeAmount?: number
  /** One-off product lines (milk lockers, DF-230), shown in their own box on the
   *  week they are delivered rather than among the day-by-day products. */
  oneOffLines?: SubscriptionLine[]
  /** Read-only view (the Dynamics iframe, DF-345): hide the per-day Edit and
   *  Pause / Resume controls. The schedule and totals still show. */
  readOnly?: boolean
  /** When set, a Refresh button appears next to the calendar toggle to force a
   *  reload from CRM (the read-only Dynamics view, DF-345). */
  onRefresh?: () => void
  refreshing?: boolean
  /** Christmas overlays (DF-345). Round moves annotate a delivery with its
   *  revised day; glass windows + opt-out drive the "delivered in Plastic" note. */
  roundAmendments?: RoundDayAmendment[]
  glassWindows?: { from: string, to: string }[]
  glassOptedOut?: boolean
  /** DF-473: deliveries Dynamics has on one date that customers are shown on
   *  another (Tuesday 22 delivered on Wednesday 23). Display only. */
  dateOverrides?: DeliveryDateOverride[]
  /**
   * Each subscription's billing frequency, keyed by its CRM id (a line's
   * subscription_external_id). A product that doesn't come every week says
   * how often it does (DF-392).
   */
  frequencyBySubscription?: Map<string, number>
}

/**
 * "Your deliveries" — what actually arrives, and what it costs.
 *
 * A two-month calendar on top with the chosen week beneath it. Picking any
 * delivery shows every delivery in that week, because a customer thinks in
 * weeks ("what am I getting, what will it cost"), and the total covers the week.
 *
 * The numbers are read-only (the standing order resolved against scheduled
 * changes and pauses), but each delivery carries a quick Pause / Resume for
 * that one day. Pausing a run of days ahead is the paused-deliveries calendar's
 * job; everything else that changes the order lives in Delivery Management.
 */
const DeliverySchedule: React.FC<DeliveryScheduleProps> = ({
  deliveries,
  loading = false,
  onSkipDay,
  onCancelSkip,
  skipDisabled = false,
  minPauseDate,
  onViewProduct,
  surchargeAmount = 0,
  oneOffLines = [],
  readOnly = false,
  onRefresh,
  refreshing = false,
  roundAmendments = [],
  glassWindows = [],
  glassOptedOut = false,
  dateOverrides = [],
  frequencyBySubscription,
}) => {
  /** How often a line's product comes, the same tag Delivery Management shows. */
  const frequencyFor = (line: SubscriptionLine): string | null =>
    frequencyTag(frequencyBySubscription?.get(line.subscription_external_id))

  /** The Monday of the selected week, as YYYY-MM-DD. */
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null)
  /** Whether the temporary-service-charge explanation is open. */
  const [showSurchargeInfo, setShowSurchargeInfo] = useState(false)
  /** The "More info" open for a Christmas delivery day (DF-478). */
  const [scheduleInfo, setScheduleInfo] = useState<ScheduleInfo | null>(null)
  /** Collapsed by default: the packshots answer "what's coming" at a glance. */
  // Details are always on now; the toggle was removed.
  const [showDetails] = useState(true)
  /** Which pair of months is on screen; 0 is this month and next. */
  const [monthOffset, setMonthOffset] = useState(0)
  /** The calendar is a chooser for jumping about, so it stays out of the way. */
  const [pickingWeek, setPickingWeek] = useState(false)
  /** One month at a time on a phone (three across is unreadable there); the
   *  pager then moves a month at a time instead of three. */
  const isMobile = useMediaQuery('(max-width: 640px)')
  const monthsShown = isMobile ? 1 : 3

  const roundMoveFrom = useMemo(
    () => new Map(roundAmendments.map(a => [a.date_from, a])),
    [roundAmendments]
  )

  /**
   * DF-473: the date customers are told a delivery arrives on. Dynamics keeps
   * some deliveries on one date that reach the doorstep on another (Christmas:
   * Tuesday 22 delivered on Wednesday 23), set in the admin.
   */
  const shownAs = useMemo(() => shownDateLookup(dateOverrides), [dateOverrides])

  /**
   * DF-345 / DF-478 Christmas changes: where each moved delivery actually
   * arrives, keyed by its own date. The day it leaves shows red ("No
   * delivery") with its products, and the day it lands on shows green.
   */
  const roundMoveByDate = useMemo(
    () => movedDeliveryTargets(roundAmendments, dateOverrides),
    [roundAmendments, dateOverrides]
  )

  /**
   * The deliveries landing on each date from a moved day, keyed by the date
   * they land on. Only a move that carries a delivery counts: one from a day
   * with nothing coming, or a day the customer has paused, brings nothing.
   */
  const movedInto = useMemo(() => {
    const map = new Map<string, DeliveryDate[]>()
    deliveries.forEach(d => {
      const to = roundMoveByDate.get(toLocalDateString(d.date))
      if (!to || d.isHoliday || d.items.length === 0) return
      map.set(to, [...(map.get(to) ?? []), d])
    })
    return map
  }, [deliveries, roundMoveByDate])

  /**
   * The deliveries to show, plus a day for any date a delivery moves to that
   * isn't one of the customer's own delivery days (a Friday-only customer whose
   * Friday moves to a Tuesday), so they can see when it will come. It lists no
   * products: those stay on their own day.
   */
  const shownDeliveries = useMemo(() => {
    const own = new Set(deliveries.map(d => toLocalDateString(d.date)))
    const today = toStartOfDay(new Date())
    const extra: DeliveryDate[] = []
    movedInto.forEach((_, to) => {
      if (own.has(to)) return
      const date = localDate(to)
      extra.push({
        id: -(extra.length + 1),
        date,
        isHoliday: false,
        isNext: false,
        isEditable: false,
        isPast: date < today,
        firstDeliveryLocked: false,
        items: [],
        allActiveLines: [],
      })
    })
    return extra.length === 0
      ? deliveries
      : [...deliveries, ...extra].sort((a, b) => a.date.getTime() - b.date.getTime())
  }, [deliveries, movedInto])

  /**
   * DF-478: the special schedule periods, shaded grey on the calendar with a
   * header ("Christmas Delivery Schedule"), worked out from the moves.
   */
  const periods = useMemo(() => schedulePeriods(roundMoveByDate), [roundMoveByDate])
  const periodAt = (key: string) => periodFor(periods, key)

  /** Dates reached only through a date override (Wednesday 23): not a day
   *  anything is normally delivered on, unlike Monday 21. */
  const overrideTargets = useMemo(() => new Set(dateOverrides.map(o => o.show_as)), [dateOverrides])

  const weekdayOf = (key: string): string => WEEKDAY_NAMES[localDate(key).getDay()]

  /** "Monday", "Monday and Thursday", "Monday, Tuesday and Friday". */
  const dayList = (days: string[], last = 'and'): string =>
    days.length > 1 ? `${days.slice(0, -1).join(', ')} ${last} ${days[days.length - 1]}` : days[0]

  /**
   * The usual delivery days whose deliveries arrive on a date: the date's own
   * day (unless it's only reached by an override, like Wednesday 23), then each
   * day moved onto it. From the round, not the customer, so the wording is the
   * same for everyone on that round.
   */
  const combinedDays = (landing: string): { own: boolean, moved: string[] } => {
    const moved: string[] = []
    Array.from(roundMoveByDate.keys()).sort().forEach(from => {
      if (roundMoveByDate.get(from) === landing && !moved.includes(weekdayOf(from))) moved.push(weekdayOf(from))
    })
    return { own: !overrideTargets.has(landing), moved: moved.filter(d => d !== weekdayOf(landing)) }
  }

  /**
   * DF-478: a day something is actually delivered on in a special schedule
   * period shows green, "DELIVERY DAY": its own products and any moved onto it.
   * The day a delivery moved away from shows red instead.
   */
  const isScheduleDeliveryDay = (delivery: DeliveryDate): boolean => {
    const key = toLocalDateString(delivery.date)
    return !!periodAt(key)
      && !roundMoveByDate.has(key)
      && !isPausedDelivery(delivery)
      && (delivery.items.length > 0 || movedInto.has(key))
  }

  /** Green day: "Monday and Thursday deliveries will be combined and delivered today." */
  const scheduleDayMessage = (delivery: DeliveryDate): string => {
    const key = toLocalDateString(delivery.date)
    const { own, moved } = combinedDays(key)
    const days = [...(own ? [weekdayOf(key)] : []), ...moved]
    if (moved.length === 0) return `Your usual ${weekdayOf(key)} delivery will arrive as normal.`
    return days.length > 1
      ? `${dayList(days)} deliveries will be combined and delivered today.`
      : `${days[0]} deliveries will be delivered today.`
  }

  /**
   * The "More info" pop-up for a day in a special schedule period (DF-478):
   * which deliveries come when, where to make changes and by when (the DF-473
   * cut-offs for the day they're actually delivered), and the glass swap.
   */
  const festiveInfo = (landing: string): ScheduleInfo => {
    const { own, moved } = combinedDays(landing)
    const day = weekdayOf(landing)
    const both = moved.length === 2 ? `${moved[0]}, ${moved[1]} or both days` : dayList(moved, 'or')

    const intro = moved.length === 0
      ? `Your ${day} delivery will take place as normal.`
      : own
        ? `If you get a ${day} delivery it will take place as normal. If you normally receive a delivery on ${dayList(moved, 'or')}, this will also be delivered on ${day}.`
        : `If you normally receive deliveries on ${both}, these will be delivered on ${day} instead.`

    const notes: string[] = []
    const original = dayList([...(own ? [day] : []), ...moved], 'or')
    // Over Christmas customers are asked to have changes in by a date set by
    // the business (what the Christmas email says), ahead of the real cut-offs,
    // which still apply. Any other period shows the real cut-offs.
    const move = roundAmendments.find(a => shownAs(a.date_to) === landing && a.change_by && a.pause_by)
    const cutoff = periodAt(landing)?.christmas && CHRISTMAS_CHANGES_BY
      ? ` The cut-off for Christmas delivery changes is ${CHRISTMAS_CHANGES_BY}.`
      : move?.change_by && move.pause_by
        ? ` The cut-off for changes is ${formatCutoffTime(move.cutoff_time)} on ${formatFullDate(move.change_by)}, and for pauses ${formatCutoffTime(move.cutoff_time)} on ${formatFullDate(move.pause_by)}.`
        : ''
    notes.push(`To make changes or pause a delivery, please select its original delivery day (${original}) in your portal.${cutoff}`)

    if (!glassOptedOut && inGlassWindow(landing)) {
      notes.push('Any products normally supplied in glass bottles will be delivered in plastic bottles instead.')
      notes.push(`If you'd prefer to receive glass bottles, please contact our Customer Service team on ${CUSTOMER_SERVICE_PHONE} to arrange this.`)
    }

    return { title: periodAt(landing)?.christmas ? 'Your Festive delivery' : 'Your delivery', intro, notes }
  }

  /** Deliveries keyed by YYYY-MM-DD so a calendar cell can find its own. */
  const byDate = useMemo(() => {
    const map = new Map<string, DeliveryDate>()
    shownDeliveries.forEach(d => map.set(toLocalDateString(d.date), d))
    return map
  }, [shownDeliveries])

  // Land on the week of the next delivery that hasn't happened yet. A new
  // customer's first delivery is locked (not editable) but is exactly the week
  // they expect to land on, so a locked first delivery counts here too
  // (FEDEV-544); otherwise the landing would skip past it to the next editable
  // week.
  useEffect(() => {
    if (deliveries.length === 0) return
    const next = deliveries.find(d => (d.isEditable || d.firstDeliveryLocked) && !d.isHoliday) ?? deliveries[0]
    setSelectedWeek(toLocalDateString(startOfWeek(next.date)))
  }, [deliveries])

  /** Every delivery in the selected week, in date order. */
  const weekDeliveries = useMemo(() => {
    if (!selectedWeek) return []
    return shownDeliveries.filter(d => toLocalDateString(startOfWeek(d.date)) === selectedWeek)
  }, [shownDeliveries, selectedWeek])

  // One-off products delivered in the selected week (milk lockers, DF-230),
  // shown in their own box rather than among the day groups.
  const weekOneOffs = useMemo(() => {
    if (!selectedWeek) return []
    return oneOffLines.filter(
      l => l.start_date && toLocalDateString(startOfWeek(new Date(l.start_date))) === selectedWeek
    )
  }, [oneOffLines, selectedWeek])

  /** The two months on screen, moved by the pager. */
  const months = useMemo(() => {
    const now = new Date()
    return Array.from({ length: monthsShown }, (_, i) => {
      const anchor = new Date(now.getFullYear(), now.getMonth() + monthOffset + i, 1)
      return {
        label: anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
        weeks: buildMonth(anchor.getFullYear(), anchor.getMonth()),
      }
    })
  }, [monthOffset, monthsShown])

  /**
   * How far the pager may run: the month of the last projected delivery. There
   * is no point offering a month with nothing in it, so the arrows stop there
   * rather than at some fixed number.
   */
  const maxMonthOffset = useMemo(() => {
    if (deliveries.length === 0) return 0
    const now = new Date()
    const last = deliveries[deliveries.length - 1].date
    const span = (last.getFullYear() - now.getFullYear()) * 12 + (last.getMonth() - now.getMonth())
    // Last window starts monthsShown-1 before the final month, so that month
    // still lands on screen (0 on mobile, where the window is a single month).
    return Math.max(0, span - (monthsShown - 1))
  }, [deliveries, monthsShown])

  const canGoBack = monthOffset > 0
  const canGoForward = monthOffset < maxMonthOffset

  /**
   * Every week that has a delivery, in order. The arrows step through these
   * rather than through calendar weeks, so they never land on an empty one and
   * they stop at the ends of what has been projected.
   */
  const weekKeys = useMemo(() => {
    const keys = new Set<string>()
    shownDeliveries.forEach(d => keys.add(toLocalDateString(startOfWeek(d.date))))
    return Array.from(keys).sort()
  }, [shownDeliveries])

  const weekIndex = selectedWeek ? weekKeys.indexOf(selectedWeek) : -1
  const canGoPreviousWeek = weekIndex > 0
  const canGoNextWeek = weekIndex >= 0 && weekIndex < weekKeys.length - 1

  const stepWeek = (by: number) => {
    const next = weekKeys[weekIndex + by]
    if (next) setSelectedWeek(next)
  }

  /**
   * Open the chooser on the months around the week being shown, not on today.
   * Someone looking at September should not have to page back to it.
   */
  const toggleWeekPicker = () => {
    setPickingWeek(open => {
      if (!open && selectedWeek) {
        const now = new Date()
        const shown = toStartOfDay(selectedWeek)
        const span = (shown.getFullYear() - now.getFullYear()) * 12
          + (shown.getMonth() - now.getMonth())
        setMonthOffset(Math.max(0, Math.min(maxMonthOffset, span)))
      }

      return !open
    })
  }

  /** One total for the week; a skipped day contributes nothing. */
  const weekTotal = useMemo(() => weekDeliveries.reduce((sum, delivery) => {
    if (delivery.isHoliday) return sum
    return sum + delivery.items.reduce(
      (daySum, line) => daySum + line.quantity * (line.unit_price_inc_vat ?? line.unit_price ?? 0),
      0
    )
  }, 0), [weekDeliveries])

  // The week's one-off products (milk lockers, DF-230) count towards this
  // week's total, shown in their own titled section above the total.
  const weekOneOffTotal = useMemo(
    () => weekOneOffs.reduce((sum, l) => sum + ((l.subtotal_inc_vat ?? l.subtotal) || 0), 0),
    [weekOneOffs]
  )

  // DF-345 Christmas overlays. A glass window plus a not-opted-out customer
  // drives the "delivered in Plastic" note on each glass line.
  const inGlassWindow = (dateStr: string): boolean =>
    glassWindows.some(w => dateStr >= w.from && dateStr <= w.to)

  // The weekly delivery surcharge applies to a week that actually has a
  // delivery, so it isn't shown on an empty or fully-paused week (DF-88).
  const weekHasDeliveries = weekDeliveries.some(d => !d.isHoliday && d.items.length > 0)
  const showSurcharge = surchargeAmount > 0 && weekHasDeliveries
  const displayedWeekTotal = weekTotal + (showSurcharge ? surchargeAmount : 0) + weekOneOffTotal
  // "12p" reads better than "£0.12" for a sub-£1 charge; keep pounds above that.
  const chargeLabel = surchargeAmount < 1
    ? `${Math.round(surchargeAmount * 100)}p`
    : formatPrice(surchargeAmount)

  const todayKey = toLocalDateString(new Date())

  const weekLabel = weekDeliveries.length > 0
    ? `Week of ${formatDayAndMonth(startOfWeek(weekDeliveries[0].date))}`
    : ''

  return (
    <div className="sun-column">
      <section className="sun-panel is-bare">

        {loading ? (
          <div className="sun-loading" role="status">
            <div className="spinner" aria-hidden="true" />
            <p>Loading your deliveries...</p>
          </div>
        ) : deliveries.length === 0 ? (
          <p className="sun-empty">No deliveries scheduled.</p>
        ) : (
          <>
            <div className="sun-week-head">
              <h3 className="sun-card-title sun-week-label">{weekLabel || 'Pick a week'}</h3>

              <div className="sun-week-nav">
                {/* Make changes and Add product moved to the rail on the right,
                    so the header keeps only the calendar and its week arrows. */}
                {/* Named rather than a bare chevron: the calendar is for
                    jumping somewhere else, which an arrow beside two other
                    arrows would not say. */}
                {onRefresh && (
                  <button
                    type="button"
                    className="sun-detail-toggle"
                    onClick={onRefresh}
                    disabled={refreshing}
                  >
                    <MdRefresh aria-hidden="true" className={refreshing ? 'sun-refresh-spin' : undefined} />
                    {refreshing ? 'Refreshing…' : 'Refresh'}
                  </button>
                )}

                <button
                  type="button"
                  className="sun-detail-toggle"
                  onClick={toggleWeekPicker}
                  aria-expanded={pickingWeek}
                  disabled={weekKeys.length === 0}
                >
                  <MdCalendarMonth aria-hidden="true" />
                  {pickingWeek ? 'Hide delivery calendar' : 'View delivery calendar'}
                </button>

                <button
                  type="button"
                  className="sun-month-nav"
                  onClick={() => stepWeek(-1)}
                  disabled={!canGoPreviousWeek}
                  aria-label="Previous week"
                >
                  <MdChevronLeft />
                </button>
                <button
                  type="button"
                  className="sun-month-nav"
                  onClick={() => stepWeek(1)}
                  disabled={!canGoNextWeek}
                  aria-label="Next week"
                >
                  <MdChevronRight />
                </button>
              </div>
            </div>

            {pickingWeek && (
            <>
            <div className="sun-cal-legend" aria-hidden="true">
              <span className="sun-cal-legend-item"><span className="sun-cal-swatch is-delivery" /> Delivery</span>
              {roundMoveByDate.size > 0 && (
                <>
                  <span className="sun-cal-legend-item"><span className="sun-cal-swatch is-moved" /> No delivery</span>
                  <span className="sun-cal-legend-item"><span className="sun-cal-swatch is-moved-to" /> Combined delivery</span>
                </>
              )}
              <span className="sun-cal-legend-item"><span className="sun-cal-swatch is-holiday" /> Paused</span>
              {Array.from(new Set(periods.map(p => p.label))).map(label => (
                <span key={label} className="sun-cal-legend-item"><span className="sun-cal-swatch is-schedule-period" /> {label}</span>
              ))}
            </div>
            <div className="sun-calendars">
              {months.map((month, i) => (
                <div key={month.label} className="sun-calendar">
                  <p className="sun-calendar-month">
                    {i === 0 && (
                      <button
                        type="button"
                        className="sun-month-nav"
                        onClick={() => setMonthOffset(o => Math.max(0, o - monthsShown))}
                        disabled={!canGoBack}
                        aria-label="Earlier months"
                      >
                        <MdChevronLeft />
                      </button>
                    )}
                    <span>{month.label}</span>
                    {i === monthsShown - 1 && (
                      <button
                        type="button"
                        className="sun-month-nav"
                        onClick={() => setMonthOffset(o => Math.min(maxMonthOffset, o + monthsShown))}
                        disabled={!canGoForward}
                        aria-label="Later months"
                      >
                        <MdChevronRight />
                      </button>
                    )}
                  </p>

                  <div className="sun-calendar-grid">
                    {WEEKDAY_INITIALS.map((initial, j) => (
                      <span key={`h-${j}`} className="sun-calendar-weekday" aria-hidden="true">
                        {initial}
                      </span>
                    ))}

                    {month.weeks.flat().map((date, j) => {
                      if (!date) return <span key={`b-${j}`} className="sun-calendar-blank" />

                      const key = toLocalDateString(date)
                      const delivery = byDate.get(key)

                      if (!delivery) {
                        return (
                          <span
                            key={key}
                            className={`sun-calendar-day${key === todayKey ? ' is-today' : ''}${periodAt(key) ? ' in-schedule-period' : ''}`}
                          >
                            {date.getDate()}
                          </span>
                        )
                      }

                      // Every delivery in the chosen week reads as selected,
                      // since the week is what the panel below is showing.
                      const isSelected = toLocalDateString(startOfWeek(date)) === selectedWeek
                      const realKey = toLocalDateString(delivery.date)

                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            setSelectedWeek(toLocalDateString(startOfWeek(date)))
                            // On a phone the calendar is a full screen of its
                            // own, so collapse it once a day is chosen to reveal
                            // that day's deliveries beneath.
                            if (isMobile) setPickingWeek(false)
                          }}
                          aria-pressed={isSelected}
                          aria-label={`${formatFullDate(date)}${isPausedDelivery(delivery) ? ', paused' : roundMoveByDate.has(realKey) ? ', rescheduled' : ', delivery'}`}
                          className={[
                            'sun-calendar-day',
                            isPausedDelivery(delivery) ? 'is-paused' : 'is-delivery',
                            roundMoveByDate.has(realKey) ? 'is-moved' : '',
                            isScheduleDeliveryDay(delivery) ? 'is-moved-to' : '',
                            periodAt(key) ? 'in-schedule-period' : '',
                            isSelected ? 'is-selected' : '',
                            key === todayKey ? 'is-today' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          {date.getDate()}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
            </>
            )}

            <div className="sun-day-detail">
              {weekDeliveries.length === 0 ? (
                <p className="sun-empty">Pick a date to see what&rsquo;s coming.</p>
              ) : (
                <>
                  {selectedWeek && periodAt(selectedWeek) && (
                    <h4 className="sun-schedule-period-title">{periodAt(selectedWeek)?.label}</h4>
                  )}
                  {weekDeliveries.map(delivery => {
                    const items = byQuantity(delivery.items)
                    const dayKey = toLocalDateString(delivery.date)
                    const movedTo = roundMoveByDate.get(dayKey)
                    const movedInMessage = isScheduleDeliveryDay(delivery) ? scheduleDayMessage(delivery) : null
                    const noDeliveryNote = movedTo && !isPausedDelivery(delivery)
                      ? `If you usually receive a delivery today, it will arrive on ${weekdayOf(movedTo)} instead.`
                      : null
                    // The day whose arrangements the "More info" explains.
                    const infoFor = movedInMessage ? dayKey : noDeliveryNote ? movedTo : null
                    const info = infoFor ? festiveInfo(infoFor) : null
                    // A moved delivery's pause closes earlier, from the day it's
                    // actually delivered on (DF-473).
                    const pauseClosed = roundMoveFrom.get(dayKey)?.pause_closed === true

                    return (
                    <div
                      key={delivery.id}
                      className={`sun-day-block${isPausedDelivery(delivery) ? ' is-skipped' : ''}${delivery.isPast ? ' is-past' : ''}${movedTo ? ' is-moved' : ''}${movedInMessage ? ' is-moved-to' : ''}`}
                    >
                      <div className="sun-day-block-head">
                        <span className="sun-day-block-date">
                          {formatFullDate(delivery.date)}
                        </span>

                        {delivery.isPast && (
                          <span className="sun-day-passed">Clink, clink! This delivery has passed!</span>
                        )}

                        {!readOnly && delivery.isEditable && (
                          <div className="sun-day-actions">
                            <Link to="/delivery-management" className="sun-skip-link">
                              <MdEdit aria-hidden="true" />
                              Edit
                            </Link>

                            {isPausedDelivery(delivery)
                              ? onCancelSkip && (
                                <button
                                  type="button"
                                  className="sun-skip-link"
                                  onClick={() => onCancelSkip(delivery.date)}
                                  disabled={skipDisabled}
                                >
                                  <MdUndo aria-hidden="true" />
                                  Resume<span className="sun-skip-word">&nbsp;delivery</span>
                                </button>
                              )
                              // Still offered on a day whose delivery has moved: the
                              // customer may want to pause it all the same (DF-345).
                              : onSkipDay && delivery.items.length > 0 && !pauseClosed && (!minPauseDate || dayKey >= minPauseDate) && (
                                <button
                                  type="button"
                                  className="sun-skip-link"
                                  onClick={() => onSkipDay(delivery.date)}
                                  disabled={skipDisabled}
                                >
                                  <MdEventBusy aria-hidden="true" />
                                  Pause<span className="sun-skip-word">&nbsp;delivery</span>
                                </button>
                              )}
                          </div>
                        )}
                      </div>

                      {/* DF-478: the day's message sits under the date and its
                          Edit / Pause, full width, with "More info" at the end. */}
                      {(noDeliveryNote || movedInMessage) && (
                        <div className={`sun-day-schedule-note${noDeliveryNote ? ' is-moved' : ' is-moved-to'}`}>
                          <span className={noDeliveryNote ? 'sun-day-nodelivery' : 'sun-day-deliveryday'}>
                            {noDeliveryNote ? 'No delivery' : 'Delivery day'}
                          </span>
                          <span className="sun-day-schedule-text">
                            {noDeliveryNote ?? movedInMessage}
                            {info && (
                              <button
                                type="button"
                                className="sun-day-moreinfo"
                                onClick={() => setScheduleInfo(info)}
                              >
                                More info
                              </button>
                            )}
                          </span>
                        </div>
                      )}

                      {isPausedDelivery(delivery) ? (
                        <p className="sun-holiday-note">Paused, nothing will be delivered.</p>
                      ) : showDetails ? (
                        <ul className="sun-delivery-items">
                          {items.map(line => (
                            <li
                              key={line.id}
                              className={line.isPending ? 'is-changed' : undefined}
                              onClick={() => onViewProduct?.(line.product_id, line.product_name || line.name, line.unit_price_inc_vat ?? line.unit_price ?? 0)}
                              role={onViewProduct ? 'button' : undefined}
                            >
                              <img
                                className="sun-item-image"
                                src={line.product_image || PRODUCT_IMAGE}
                                alt=""
                                onError={(e) => { e.currentTarget.src = PRODUCT_IMAGE }}
                              />
                              <span className="sun-item-text">
                                {/* Name, then how often it comes (DF-392) and, in a glass
                                    window, the Plastic note (DF-345), each on its own line. */}
                                <span className="sun-item-namecol sun-item-label">
                                  <span className="sun-item-name">{line.product_name || line.name}</span>
                                  {frequencyFor(line) && (
                                    <span className="ddc-freq">{frequencyFor(line)}</span>
                                  )}
                                  {/* In a special schedule period the day's message already
                                      says glass comes in plastic (DF-478), so not again here. */}
                                  {line.is_glass && !glassOptedOut && inGlassWindow(dayKey) && !periodAt(dayKey) && (
                                    <span className="sun-item-plastic">To be delivered in Plastic for this delivery</span>
                                  )}
                                </span>
                                <span className="sun-item-qty">
                                  {line.quantity} &times; {formatPrice(line.unit_price_inc_vat ?? line.unit_price ?? 0)}
                                </span>
                              </span>
                              <span className="sun-item-total">
                                {formatPrice(line.quantity * (line.unit_price_inc_vat ?? line.unit_price ?? 0))}
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <ul className="sun-item-thumbs">
                          {items.map(line => (
                            <li
                              key={line.id}
                              className={line.isPending ? 'is-changed' : undefined}
                              title={`${line.quantity} × ${line.product_name || line.name}`}
                              onClick={() => onViewProduct?.(line.product_id, line.product_name || line.name, line.unit_price_inc_vat ?? line.unit_price ?? 0)}
                              role={onViewProduct ? 'button' : undefined}
                            >
                              <img
                                src={line.product_image || PRODUCT_IMAGE}
                                alt={line.product_name || line.name || 'Product'}
                                onError={(e) => { e.currentTarget.src = PRODUCT_IMAGE }}
                              />
                              {/* Only when there is more than one. Every item is
                                  at least one, so a "1" badge on every
                                  thumbnail is noise that buries the counts
                                  that do matter. */}
                              {line.quantity > 1 && (
                                <span className="sun-thumb-qty">{line.quantity}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    )
                  })}

                  {/* One-off products delivered this week (DF-230): their own
                      titled section, above the total, which they count towards. */}
                  <OneOffDeliveriesBox lines={weekOneOffs} variant="week" readOnly={readOnly} />

                  <div className="sun-delivery-total-row">
                    <span className="sun-delivery-total-label">Weekly total</span>
                    <span className="sun-delivery-total">{formatPrice(displayedWeekTotal)}</span>
                  </div>

                  {showSurcharge && (
                    <div className="sun-surcharge">
                      <span className="sun-surcharge-line">
                        Includes {chargeLabel} temporary service charge
                        <button
                          type="button"
                          className="sun-surcharge-info-btn"
                          aria-label="What is the temporary service charge?"
                          aria-expanded={showSurchargeInfo}
                          onClick={() => setShowSurchargeInfo(open => !open)}
                        >
                          i
                        </button>
                      </span>
                      {showSurchargeInfo && (
                        <div className="sun-surcharge-info" role="note">
                          <strong>What is this?</strong> Like many businesses, we&apos;ve
                          experienced increases in the cost of producing and delivering our
                          products. This temporary {chargeLabel} weekly charge helps us manage
                          some of these additional costs while continuing to provide your
                          doorstep delivery service.
                          <br /><br />
                          We&apos;ll continue to review the charge and will remove it when
                          circumstances allow.
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </section>

      <ScheduleInfoModal info={scheduleInfo} onClose={() => setScheduleInfo(null)} />
    </div>
  )
}

export default DeliverySchedule
